import React from 'react';

/**
 * One tab in a detail drawer's tab strip (Activity · Scores · Conversations).
 *
 * The run, span and conversation drawers each had their own copy of this
 * button, kept identical by hand because the drawers stack on top of each
 * other. They now share it.
 *
 * Underline, not the SegmentedTabs pill: these tabs are sections of one
 * drawer, while SegmentedTabs switches the grain of the same data (runs vs
 * spans, tree vs timeline) and sits right beside them — two different jobs,
 * two different shapes.
 *
 * The active tab is marked by the theme's selected-state tint
 * (`surface-selected`, orange-derived) behind ordinary text, with no
 * underline. A text-coloured bar read as a stark white rule on the dark
 * surface; orange text alone is ~2.9:1 on white at this size, too faint to
 * read as "selected".
 */
export default function DrawerTab({ id, label, badge, active, onClick, role }) {
  // `role="tab"` where the strip is a real tablist; it then reports selection
  // with aria-selected instead of aria-current.
  const a11y = role === 'tab'
    ? { role: 'tab', 'aria-selected': !!active }
    : { 'aria-current': active ? 'page' : undefined };
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      {...a11y}
      className={`my-1.5 whitespace-nowrap rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors ${
        active
          ? 'bg-surface-selected text-[color:var(--text-primary)]'
          : 'text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]'
      }`}
    >
      <span className="inline-flex items-center gap-1.5">
        {label}
        {badge != null ? (
          <span className={`rounded-full px-1.5 text-[10px] font-semibold tabular-nums ${
            active ? 'bg-surface text-[color:var(--text-primary)]' : 'bg-[color:var(--border-subtle)] text-[color:var(--text-secondary)]'
          }`}>
            {badge}
          </span>
        ) : null}
      </span>
    </button>
  );
}
